import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Lock, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { preventRunts } from '../lib/typography';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WikiDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEntry();
  }, [id]);

  const fetchEntry = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wiki/entries/${id}`, {
        withCredentials: true
      });
      setEntry(data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('This is members-only content. Please log in to view.');
      } else {
        setError('Entry not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/wiki/entries/${id}`, {
        withCredentials: true
      });
      navigate('/wiki');
    } catch (err) {
      alert('Error deleting entry');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center" data-testid="entry-loading">
        <div className="text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-6" data-testid="entry-error">
        <div className="text-center max-w-md">
          <Lock className="w-16 h-16 text-primary mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-2xl font-serif text-foreground mb-4">{error}</h2>
          {!user && (
            <div className="flex gap-4 justify-center">
              <Link to="/login" data-testid="error-login-link">
                <Button>Log In</Button>
              </Link>
              <Link to="/register" data-testid="error-register-link">
                <Button variant="outline">Sign Up</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!entry) return null;

  const canEdit = user && (user._id === entry.author_id || user.role === 'admin');

  return (
    <div className="min-h-[calc(100vh-80px)] py-12 px-6" data-testid="wiki-detail-page">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/wiki/the-growling-griffon" className="inline-flex items-center gap-2 text-primary hover:underline font-sans" data-testid="back-to-wiki-link">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Main Entry
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/wiki" className="inline-flex items-center gap-2 text-primary hover:underline font-sans" data-testid="view-all-entries-link">
            Full Glossary
          </Link>
        </div>

        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-4xl lg:text-5xl font-serif tracking-tighter text-primary mb-4" data-testid="entry-title">
                {entry.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="secondary" className="rounded-sm font-sans">
                  {entry.category}
                </Badge>
                {entry.is_members_only && (
                  <Badge variant="outline" className="rounded-sm font-sans border-primary text-primary">
                    <Lock className="w-3 h-3 mr-1" aria-hidden="true" />
                    Members Only
                  </Badge>
                )}
              </div>
            </div>
            
            {canEdit && (
              <div className="flex gap-2" data-testid="entry-actions">
                <Link to={`/wiki/${id}/edit`} data-testid="edit-entry-link">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Edit className="w-4 h-4" aria-hidden="true" />
                    Edit
                  </Button>
                </Link>
                <Button variant="destructive" size="sm" onClick={handleDelete} className="gap-2" data-testid="delete-entry-button">
                  <Trash2 className="w-4 h-4" aria-hidden="true" />
                  Delete
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground font-sans border-t border-b border-border py-3">
            <span>By {entry.author_name}</span>
            <span>•</span>
            <span>{new Date(entry.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            {entry.updated_at !== entry.created_at && (
              <>
                <span>•</span>
                <span>Updated {new Date(entry.updated_at).toLocaleDateString()}</span>
              </>
            )}
          </div>
        </div>

        <div
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: preventRunts(entry.content) }}
          data-testid="entry-content"
        />
      </div>
    </div>
  );
};

export default WikiDetailPage;
